import { Injectable } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ActionsService } from '@core/actions-modals/actions.service';
import { AuthService } from '@features/auth/auth.service';
import { ToastStatusEnum } from '@ui-kit/app-toast/toast-status.enum';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { handleError } from '@ui-kit/utility/handleHttpErrorResponse';
import { Collection, CrudService, Item, ItemModel } from 'collection';
import { iif, of, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { StatusTypesEnum } from '@features/absence/overview/requests-list/requests-list.component';
import { requestStatuses } from '@features/absence/overview/requests-list/statuses.enum';

@Injectable({
  providedIn: 'root'
})
export class AbsenceActionsService {
  absenceItem: ItemModel = new Item({
    api: this._crud.createEntity({ name: 'absence' }),
    params: {
      expand: 'absenceType,user'
    }
  });

  collection = new Collection({
    api: this._crud.createEntity({ name: 'absence' })
  });

  deleteAbsenceVacation(absence) {
    const confirmSubject = new Subject();

    const config = {
      title: 'Are you sure you want to delete this item?',
      description: 'You are not able to undo this action',
      confirmButtonText: 'Yes, I confirm'
    };

    const authUserId = this._auth.currentUser.id;
    const initProfile$ = (res) =>
      of(res).pipe(
        tap(() => {
          this._auth.initProfile();
          return res;
        })
      );

    const successMessage = {
      type: ToastStatusEnum.Success,
      message: marker('Absence was successfully deleted')
    };

    this._actionsService
      .confirm(config)
      .pipe(
        take(1),
        filter(({ confirmed }) => confirmed),
        tap(() => (this.absenceItem.data = { ...absence })),
        mergeMap(({ modal }) =>
          this.absenceItem.deleteItem().pipe(
            map(() => confirmSubject.next({ item: absence })),
            map(() => this._toastService.show(successMessage)),
            catchError(({ error }) => handleError(error, this._toastService)),
            map(() => {
              this.absenceItem.clear();
              modal.close();
            })
          )
        )
      )
      .subscribe();

    return confirmSubject.pipe(
      switchMap(({ action, item }) =>
        iif(() => item.user_id === authUserId, initProfile$({ action, item }), of({ action, item }))
      )
    );
  }

  deleteAbsenceVacationMany(ids: Array<number>) {
    const confirmSubject = new Subject();

    const titlePlural = marker('Are you sure you want to delete these items?');
    const title = marker('Are you sure you want to delete this item?');
    const config = {
      title: ids.length > 1 ? titlePlural : title,
      description: 'You are not able to undo this action',
      confirmButtonText: 'Yes, I confirm'
    };

    const successMessage = {
      type: ToastStatusEnum.Success,
      message: marker('Changes are successfully saved')
    };

    const dataToDelete = { ids: ids.join(',') };

    this._actionsService
      .confirm(config)
      .pipe(
        take(1),
        filter(({ confirmed }) => confirmed),
        switchMap(({ modal }) =>
          this.collection.deleteMany({ data: dataToDelete, path: 'absence/batch-delete' }).pipe(
            map((res) => confirmSubject.next(res)),
            map(() => this._toastService.show(successMessage)),
            catchError(({ error }) => handleError(error, this._toastService)),
            map(() => modal.close())
          )
        )
      )
      .subscribe();

    return confirmSubject;
  }

  updateAbsenceVacationMany(items, status) {
    const confirmSubject = new Subject();

    const title = `Are you sure you want to set ${items.length > 1 ? 'these requests' : 'this request'} as ${status === StatusTypesEnum.Approved ? StatusTypesEnum.Approved : StatusTypesEnum.Canceled}?`;
    const config = {
      title: title,
      description: 'You are not able to undo this action',
      confirmButtonText: 'Yes, I confirm'
    };

    const dataToUpdate = {
      ids: items,
      status: status
    };

    const successMessage = {
      type: ToastStatusEnum.Success,
      message: marker('Changes are successfully saved')
    };

    const dataToDelete = {
      data: dataToUpdate,
      path: 'absence/batch-update'
    };

    this._actionsService
      .confirm(config)
      .pipe(
        take(1),
        filter(({ confirmed }) => confirmed),
        switchMap(({ modal }) =>
          this.collection.updateItem(dataToDelete).pipe(
            map((res) => confirmSubject.next(res)),
            map(() => this._toastService.show(successMessage)),
            catchError(({ error }) => handleError(error, this._toastService)),
            map(() => modal.close())
          )
        )
      )
      .subscribe();

    return confirmSubject;
  }

  changeAbsenceVacationStatus(item, action: requestStatuses) {
    const confirmSubject = new Subject();

    const title =
      requestStatuses.Approve === action
        ? marker('Are you sure you want to confirm this absence request?')
        : marker('Are you sure you want to decline this absence request?');

    const config = {
      title,
      confirmButtonText: 'Confirm',
      sizes: 'sm'
    };

    const successMessage = {
      type: ToastStatusEnum.Success,
      message: marker('Status successfully changed')
    };

    const paramsToUpdate = {
      id: item.id,
      data: {
        ...item,
        status: action
      }
    };

    item.status = status;

    this._actionsService
      .confirm(config)
      .pipe(
        take(1),
        filter(({ confirmed }) => confirmed),
        switchMap(({ modal }) =>
          this.collection.updateItem(paramsToUpdate).pipe(
            map((res) => confirmSubject.next(res)),
            map(() => this._toastService.show(successMessage)),
            catchError(({ error }) => handleError(error, this._toastService)),
            map(() => modal.close())
          )
        )
      )
      .subscribe();

    return confirmSubject;
  }

  constructor(
    private _auth: AuthService,
    private _crud: CrudService,
    private _actionsService: ActionsService,
    private _toastService: ToasterService
  ) {
    this.collection.hardReloadAfter.deleting = false;
  }
}
